import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { IconButton, Stack, Tooltip, Typography } from "@mui/material"
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"

import type { Powerline } from "types/powerlines.types"

import useRoutes from "helpers/hooks/useRoutes"

interface SpanPaginationProps {
  spanId: number
  powerline?: Powerline
}

/**
 * Walks the powerline's ordered `span_ids`, so "next" means the physically
 * adjacent span rather than the next id.
 */
const SpanPagination = ({ spanId, powerline }: SpanPaginationProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { baseOrgURL } = useRoutes()

  const spanIds = powerline?.span_ids
  const position = spanIds?.indexOf(spanId) ?? -1

  if (!spanIds || position === -1) {
    return null
  }

  const previousId = position > 0 ? spanIds[position - 1] : undefined
  const nextId = position < spanIds.length - 1 ? spanIds[position + 1] : undefined

  const goTo = (id: number) => () => navigate(`${baseOrgURL}/spans/${id}`)

  return (
    <Stack flexDirection="row" alignItems="center" columnGap={0.5}>
      <Tooltip title={t("spans.PREVIOUS_SPAN")} arrow>
        <span>
          <IconButton
            size="small"
            disabled={previousId === undefined}
            onClick={previousId === undefined ? undefined : goTo(previousId)}
            aria-label={t("spans.PREVIOUS_SPAN")}
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
        {position + 1} / {spanIds.length}
      </Typography>
      <Tooltip title={t("spans.NEXT_SPAN")} arrow>
        <span>
          <IconButton
            size="small"
            disabled={nextId === undefined}
            onClick={nextId === undefined ? undefined : goTo(nextId)}
            aria-label={t("spans.NEXT_SPAN")}
          >
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}

export default SpanPagination
